/**
 * Phase 7 — Konflikt-Behandlung (parallel, nicht sequenziell).
 *
 * Production code path:
 *  - worker/src/handlers/conflict.ts triggert nach OCR (state=ocr_complete)
 *  - Erkennt Duplikate: gleicher employee_id + month existiert bereits
 *  - Setzt state = pending_conflict
 *  - ConflictPage zeigt beide Karten side-by-side
 *  - User wählt: keep this, keep other, merge, delete
 *  - hub/src/routes/timesheets.ts POST /:id/resolve-conflict
 *
 * Output: Karte bleibt erhalten oder gelöscht; state zurück zu ocr_complete oder gar nicht
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { WerkstattCardContext } from "../../pages/WerkstattPage";
import { PipelineStepList, type PipelineStep } from "../SettingsTestOcr";
import { PhaseHeader, withUuids } from "./Phase1Upload";
import { ConflictResolver } from "./ConflictResolver";

interface ConflictInfo {
  cards: Array<{ id: string; state: string; month: string; employee_id: string | null; created_at: string }>;
}

export function Phase7Konflikt({ ctx }: { ctx: WerkstattCardContext }) {
  const [conflicts, setConflicts] = useState<ConflictInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const c = ctx.card;

  useEffect(() => {
    if (!c?.employee_id || !c?.month) { setConflicts(null); return; }
    fetch(`api/timesheets?employee_id=${c.employee_id}&month=${c.month}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setConflicts)
      .catch(e => setErr(e.message));
  }, [c?.employee_id, c?.month]);

  const otherCards = conflicts?.cards?.filter(o => o.id !== c?.id) ?? [];
  const isActiveConflict = c?.state === "pending_conflict";

  // Resolution-Audit aus data.conflict_resolved (wird vom Worker beim Resolve gesetzt)
  const resolved = (ctx.timesheet?.data as any)?.conflict_resolved ?? null;
  const resolvedAction = resolved?.action as string | undefined;  // discard / overwrite / reassign
  const resolvedAt = resolved?.resolved_at as string | undefined;
  const wasInConflict = !!resolved || isActiveConflict;

  const steps: PipelineStep[] = [
    {
      num: 1,
      title: "Conflict-Detection nach OCR",
      description:
        "Worker pre-check vor state-Write: prüft ob für employee+month bereits eine "
        + "aktive Karte existiert. Bei Hit: state=pending_conflict statt ocr_complete "
        + "(sonst feuert Unique-Constraint). Quelle: worker/src/handlers/ocr.ts pre-check.",
      status: !c ? "pending"
            : isActiveConflict ? "error"
            : wasInConflict ? "done"
            : "done",
      detail: !c ? "(wartet)"
            : isActiveConflict ? "⚠ Aktiver Konflikt — Resolver weiter unten / in Phase 2"
            : wasInConflict ? "✓ Konflikt war erkannt und wurde aufgelöst"
            : "Kein Konflikt für diese Karte",
    },
    {
      num: 2,
      title: "ConflictPage / ConflictResolver sichtbar",
      description:
        "Wenn pending_conflict: ConflictResolver erscheint in Phase 2 + Phase 7 + auf "
        + "/conflicts/timesheet/:id (drei Renderpfade, ein Resolver-Component).",
      status: !c ? "pending"
            : isActiveConflict ? "active"
            : wasInConflict ? "done"
            : "done",
      detail: !c ? "(wartet)"
            : isActiveConflict ? "User-Entscheidung erforderlich (Buttons unten)"
            : wasInConflict ? "✓ Resolver wurde benutzt"
            : "n/a (kein Konflikt für diese Karte)",
      optional: !wasInConflict,
    },
    {
      num: 3,
      title: "Resolve: overwrite / discard / reassign",
      description:
        "User-Action wird via POST /api/timesheets/:id/resolve-conflict gepostet. "
        + "Worker setzt state entsprechend + persistiert Audit nach data.conflict_resolved "
        + "(action + resolved_at + previous_conflict). Source: hub/src/routes/timesheets.ts:178.",
      status: !c ? "pending"
            : isActiveConflict ? "pending"
            : resolvedAction ? "done"
            : "done",
      detail: !c ? "(wartet)"
            : isActiveConflict ? "⏸ User muss eine Aktion wählen"
            : resolvedAction
              ? `✓ ${resolvedAction} ausgeführt ${resolvedAt ? `am ${new Date(resolvedAt).toLocaleString("de-AT")}` : ""} · neuer state=${c.state}`
              : "n/a (kein Konflikt für diese Karte)",
      optional: !wasInConflict,
    },
  ];

  return (
    <div>
      <PhaseHeader
        num={7}
        title="Konflikt-Behandlung (parallel)"
        purpose="Doppel-Erfassung erkennen + auflösen bevor Berechnung läuft"
        inputs="employee+month-Kollision"
        outputs="eine Karte überlebt mit state=ocr_complete, andere gelöscht/gemerged"
        prodFiles={[
          "worker/src/handlers/conflict.ts",
          "frontend/src/pages/ConflictPage.tsx",
          "hub/src/routes/timesheets.ts",
        ]}
        userGates="(1) ConflictPage öffnen  (2) Aktion wählen (keep/merge/delete)"
      />
      {c && (
        <ConflictResolver
          card={c}
          timesheet={ctx.timesheet}
          employees={ctx.employees}
          onResolved={ctx.reload}
        />
      )}

      <PipelineStepList steps={withUuids(c?.id ?? null, "konflikt", steps)} />

      {otherCards.length > 0 && (
        <div style={{ marginTop: 16, padding: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 6 }}>
          <strong>Andere Karten für {ctx.employee?.last_name}, {ctx.employee?.first_name} · {c?.month}:</strong>
          <ul style={{ marginTop: 8 }}>
            {otherCards.map(o => (
              <li key={o.id} style={{ fontSize: 13 }}>
                <code>{o.id.slice(0, 8)}</code> · state={o.state} · created {o.created_at}
                {" "}
                {(o.state === "pending_conflict" || c?.state === "pending_conflict") && (
                  <Link to={`/conflicts/timesheet/${c?.id ?? o.id}`}>→ ConflictPage</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {err && <div style={{ color: "#dc2626", marginTop: 8 }}>Fehler: {err}</div>}
    </div>
  );
}
